import { 
  AISuggestion, 
  Item, 
  Project, 
  Section, 
  Subsection, 
  SuggestionType 
} from '../models/types';
// We'll use a simple uuid generator until we install dependencies
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Service for AI-assisted content generation and enhancement
 */
export class AIService {
  /**
   * Generate content suggestions as user types
   * @param currentText Partial text that user is typing
   * @param context Context information about where this text is being entered
   * @returns An array of text suggestions to display
   */
  public generateCompletionSuggestions(
    currentText: string,
    context: {
      sectionId: string;
      subsectionId: string;
      itemId?: string;
      itemType?: 'title' | 'description' | 'notes';
      project: Project;
    }
  ): string[] {
    // In a real implementation, this would call an LLM API
    // For demo purposes, we'll return mock suggestions
    
    if (!currentText || currentText.length < 3) return [];
    
    const { sectionId, subsectionId, itemId, itemType, project } = context;
    
    // Identify the current section and subsection to create context-aware completions
    const section = project.sections.find(s => s.id === sectionId);
    const subsection = section?.subsections.find(s => s.id === subsectionId);
    const item = itemId ? subsection?.items.find(i => i.id === itemId) : undefined;
    
    // Context-aware suggestions based on section/subsection
    if (section?.name.includes("Vision")) {
      if (currentText.includes("platform")) {
        return [
          "platform will streamline business operations by automating key workflows",
          "platform will revolutionize how users interact with their data through an intuitive interface",
          "platform will provide a unified view of all customer interactions"
        ];
      }
      
      if (currentText.includes("goal")) {
        return [
          "goal is to increase user productivity by 25% within the first year",
          "goal is to reduce operational costs while improving customer satisfaction",
          "goal is to establish a market-leading position in the industry within 24 months"
        ];
      }
    }
    
    if (section?.name.includes("Security")) {
      if (currentText.includes("authentication")) {
        return [
          "authentication will use OAuth 2.0 with OpenID Connect for single sign-on capabilities",
          "authentication system will support multi-factor authentication for sensitive operations",
          "authentication will include support for social login providers while maintaining security"
        ];
      }
      
      if (currentText.includes("data")) {
        return [
          "data will be encrypted at rest using AES-256 and in transit using TLS 1.3",
          "data classification system will identify and protect sensitive information",
          "data access will be governed by role-based access control"
        ];
      }
    }
    
    // Generic suggestions if no specific context matches
    const suggestions = [
      `${currentText} will be implemented according to industry best practices`,
      `${currentText} should be aligned with the overall project objectives`,
      `${currentText} needs to be carefully considered for long-term maintainability`
    ];
    
    return suggestions;
  }

  /**
   * Create a formal suggestion for improving content quality
   * @param item The item to analyze for quality
   * @param section The section containing the item
   * @param subsection The subsection containing the item
   * @returns Quality improvement suggestion if applicable
   */
  public generateQualitySuggestion(
    item: Item,
    section: Section,
    subsection: Subsection
  ): AISuggestion | null {
    // In a real implementation, this would analyze content quality using an LLM
    // For demo purposes, we'll check for some basic quality issues
    
    if (!item.description || item.description.length < 20) {
      return {
        id: generateUUID(),
        targetId: item.id,
        targetType: "ITEM",
        suggestionType: SuggestionType.Quality,
        content: "This description is too brief. Consider adding more details about what this means, why it's important, and how it should be implemented.",
        accepted: false,
        generatedAt: new Date()
      };
    }
    
    if (item.description.includes("etc") || item.description.includes("...")) {
      return {
        id: generateUUID(),
        targetId: item.id,
        targetType: "ITEM",
        suggestionType: SuggestionType.Quality,
        content: "Avoid using 'etc' or '...' in requirements. Be specific about all included items.",
        accepted: false,
        generatedAt: new Date()
      };
    }
    
    const vaguePhrases = ["good", "fast", "efficient", "user-friendly", "scalable", "secure"];
    for (const phrase of vaguePhrases) {
      if (item.description.toLowerCase().includes(phrase)) {
        return {
            id: generateUUID(),
          targetId: item.id,
          targetType: "ITEM",
          suggestionType: SuggestionType.Quality,
          content: `The term '${phrase}' is subjective. Consider replacing with measurable criteria, e.g., response time < 200ms instead of 'fast'.`,
          accepted: false,
          generatedAt: new Date()
        };
      }
    }
    
    return null;
  }

  /**
   * Identify gaps in the requirements coverage
   * @param project The current project
   * @returns Array of gap analysis suggestions
   */
  public performGapAnalysis(project: Project): AISuggestion[] {
    const suggestions: AISuggestion[] = [];
    
    // Check for empty sections or subsections
    project.sections.forEach(section => {
      let hasCompletedItems = false;
      
      section.subsections.forEach(subsection => {
        const completedItems = subsection.items.filter(item => 
          item.status === "COMPLETED" || item.status === "IN_PROGRESS"
        );
        
        if (completedItems.length > 0) {
          hasCompletedItems = true;
        }
        
        // If no items are completed or in progress in this subsection
        if (subsection.items.length > 0 && completedItems.length === 0) {
          suggestions.push({
          id: generateUUID(),
            targetId: subsection.id,
            targetType: "SUBSECTION",
            suggestionType: SuggestionType.GapAnalysis,
            content: `No progress has been made in the "${subsection.name}" subsection. This area should be addressed to ensure comprehensive coverage.`,
            accepted: false,
            generatedAt: new Date()
          });
        }
      });
      
      // If no items are completed or in progress in this entire section
      if (!hasCompletedItems) {
        suggestions.push({
            id: generateUUID(),
          targetId: section.id,
          targetType: "SECTION",
          suggestionType: SuggestionType.GapAnalysis,
          content: `The "${section.name}" section has no progress. This is a key area of the framework and should be addressed.`,
          accepted: false,
          generatedAt: new Date()
        });
      }
    });
    
    // Look for missing required items
    project.sections.forEach(section => {
      section.subsections.forEach(subsection => {
        const missingRequiredItems = subsection.items.filter(item => 
          item.isRequired && item.status === "NOT_STARTED"
        );
        
        if (missingRequiredItems.length > 0) {
          missingRequiredItems.forEach(item => {
            suggestions.push({
              id: generateUUID(),
              targetId: item.id,
              targetType: "ITEM",
              suggestionType: SuggestionType.GapAnalysis,
              content: `"${item.title}" is a required item that hasn't been started yet. This should be prioritized.`,
              accepted: false,
              generatedAt: new Date()
            });
          });
        }
      });
    });
    
    return suggestions;
  }

  /**
   * Identify potential relationships between different requirements
   * @param item The current item
   * @param project The full project context
   * @returns Cross-reference suggestions
   */
  public findCrossReferences(item: Item, project: Project): AISuggestion[] {
    const suggestions: AISuggestion[] = [];
    
    // In a real implementation, this would use semantic analysis
    // For demo, we'll do simple keyword matching
    
    const searchTerms = this.extractKeywords(item.description || '');
    if (searchTerms.length === 0) return suggestions;
    
    project.sections.forEach(section => {
      section.subsections.forEach(subsection => {
        subsection.items.forEach(otherItem => {
          // Don't compare with self
          if (otherItem.id === item.id) return;
          
          // Check if any keywords match
          const otherItemTerms = this.extractKeywords(otherItem.description || '');
          const matchingTerms = searchTerms.filter(term => 
            otherItemTerms.includes(term)
          );
          
          if (matchingTerms.length > 0) {
            suggestions.push({
      id: generateUUID(),
              targetId: item.id,
              targetType: "ITEM",
              suggestionType: SuggestionType.CrossReference,
              content: `This item seems related to "${otherItem.title}" in ${section.name} > ${subsection.name}. Consider creating a relationship between these items.`,
              accepted: false,
              generatedAt: new Date()
            });
          }
        });
      });
    });
    
    return suggestions;
  }

  /**
   * Generate draft content for an empty item
   * @param item The item needing content
   * @param section The section containing the item
   * @param subsection The subsection containing the item
   * @returns Content generation suggestion
   */
  public generateInitialContent(
    item: Item,
    section: Section,
    subsection: Subsection
  ): AISuggestion | null {
    // Only generate for items without descriptions
    if (item.description && item.description.length > 0) return null;
    
    let suggestedContent = "";
    
    // Generate different content based on the section and item title
    if (section.name.includes("Vision")) {
      if (item.title.includes("Vision Statement")) {
        suggestedContent = "Our platform will provide a comprehensive solution for [specific industry/domain] that enables [key user group] to [primary value proposition] through an intuitive, scalable, and secure application.";
      } else if (item.title.includes("Business Objectives")) {
        suggestedContent = "1. Increase operational efficiency by automating key workflows\n2. Reduce manual errors by 50% within 6 months of implementation\n3. Improve customer satisfaction scores by providing faster response times\n4. Generate new revenue streams through premium feature offerings";
      }
    } else if (section.name.includes("User Analysis")) {
      if (item.title.includes("User Personas")) {
        suggestedContent = "Primary Persona: [Role Name]\n\nDemographics:\n- Age: [age range]\n- Role: [job title/role]\n- Technical proficiency: [level]\n\nGoals:\n- [Primary goal]\n- [Secondary goal]\n\nPain Points:\n- [Key frustration]\n- [Secondary frustration]\n\nUsage Patterns:\n- [When and how they would use the system]";
      }
    } else if (section.name.includes("Security")) {
      if (item.title.includes("Authentication")) {
        suggestedContent = "The system will implement OAuth 2.0 with OpenID Connect for authentication. Multi-factor authentication will be required for administrative functions and sensitive data access. Password policies will enforce strong passwords with a minimum length of 12 characters, combining uppercase, lowercase, numbers, and special characters.";
      }
    }
    
    // If no specific content was generated, create a generic placeholder
    if (!suggestedContent) {
      suggestedContent = `[Provide a detailed description of ${item.title.toLowerCase()} here, including specifics about what it entails, why it's important, and any key considerations for implementation.]`;
    }
    
    return {
      id: generateUUID(),
      targetId: item.id,
      targetType: "ITEM",
      suggestionType: SuggestionType.ContentGeneration,
      content: suggestedContent,
      accepted: false,
      generatedAt: new Date()
    };
  }

  /**
   * Calculate the progress percentage of a project based on item status and weights
   * @param project The project to calculate progress for
   * @returns Updated project with progress percentages
   */
  public calculateProgress(project: Project): Project {
    // Create a deep copy to avoid mutating the original
    const updatedProject = JSON.parse(JSON.stringify(project)) as Project;
    
    let totalSectionWeight = 0;
    let totalSectionProgress = 0;
    
    // Calculate progress for each section
    updatedProject.sections.forEach(section => {
      let totalSubsectionWeight = 0;
      let totalSubsectionProgress = 0;
      
      // Calculate progress for each subsection
      section.subsections.forEach(subsection => {
        let totalItemWeight = 0;
        let totalItemProgress = 0;
        
        // Calculate progress for each item
        subsection.items.forEach(item => {
          const progressValue = this.getProgressValueForStatus(item.status);
          totalItemProgress += item.weight * progressValue;
          totalItemWeight += item.weight;
        });
        
        // Calculate subsection progress percentage
        subsection.completionPercentage = totalItemWeight > 0 
          ? (totalItemProgress / totalItemWeight) * 100 
          : 0;
        
        totalSubsectionProgress += subsection.weight * subsection.completionPercentage;
        totalSubsectionWeight += subsection.weight;
      });
      
      // Calculate section progress percentage
      section.completionPercentage = totalSubsectionWeight > 0 
        ? (totalSubsectionProgress / totalSubsectionWeight) 
        : 0;
      
      totalSectionProgress += section.weight * section.completionPercentage;
      totalSectionWeight += section.weight;
    });
    
    // Calculate overall project progress
    updatedProject.overallProgress = totalSectionWeight > 0 
      ? (totalSectionProgress / totalSectionWeight) 
      : 0;
    
    return updatedProject;
  }

  /**
   * Extract meaningful keywords from text for analysis
   * @param text The text to extract keywords from
   * @returns Array of keywords
   */
  private extractKeywords(text: string): string[] {
    // In a real implementation, this would use NLP techniques
    // For demo, we'll do simple word extraction and filtering
    
    // Split into words and filter out common words
    const commonWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'with', 'by', 'of', 'is', 'are', 'be', 'will'];
    const words = text.toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .split(/\s+/) // Split by whitespace
      .filter(word => 
        word.length > 3 && // Only words longer than 3 characters
        !commonWords.includes(word) // Filter out common words
      );
    
    // Return unique words (avoiding spread operator for compatibility)
    return Array.from(new Set(words));
  }

  /**
   * Convert item status to a numerical progress value
   * @param status The item status
   * @returns Progress value between 0 and 1
   */
  private getProgressValueForStatus(status: string): number {
    switch (status) {
      case "COMPLETED":
        return 1.0;
      case "IN_PROGRESS":
        return 0.5;
      case "SKIPPED":
        return 1.0; // Skipped items count as complete for progress calculation
      case "NOT_STARTED":
      default:
        return 0.0;
    }
  }
}

export const aiService = new AIService();
